import { buildOptionsFromLabels, isCustomAttributeType } from '@provision/attribute-utils';
import { CustomAttributeDefinition } from '@provision/entity-types';
import { useMemo } from 'react';
import { useCustomAttributeDefinitions } from 'src/graphql/entities/custom-attributes/custom-attributes';
import { CustomAttributesEntity } from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import {
  mapAttributesDefinition,
  mapAttributesDefinitionGroups,
} from 'src/graphql/entities/custom-attributes/custom-attributes.mappings';
import {
  CustomAttributeOption,
  getCustomAttributeKey,
  NormalizedCustomAttributeDefinition,
  NormalizedCustomAttributeGroup,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { parseOptionsInput } from 'src/graphql/entities/custom-attributes/parse-options-input';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';

interface UseNormalizedCustomAttributeDefinitionsParams {
  partnerId: number | undefined;
  entity: CustomAttributesEntity;
}

interface UseNormalizedCustomAttributeDefinitionsResult {
  allDefinitions: NormalizedCustomAttributeDefinition[];
  visibleDefinitions: NormalizedCustomAttributeDefinition[];
  groups: NormalizedCustomAttributeGroup[];
  loading: boolean;
}

/**
 * fetches the raw custom-attribute definitions for the given entity/partner
 * and normalizes them into the shape the UI works with: stable keys,
 * resolved type ids, parsed groups, and visibility metadata. Unsupported
 * types (e.g. introduced by a newer backend) are dropped with a warning.
 * Returns both `allDefinitions` and `visibleDefinitions` (filtered by the
 * current user's group rules), plus the list of defined groups.
 */
export function useNormalizedCustomAttributeDefinitions({
  partnerId,
  entity,
}: UseNormalizedCustomAttributeDefinitionsParams): UseNormalizedCustomAttributeDefinitionsResult {
  const { user } = useCurrentUserProfile();

  const { customAttributeDefinitions, loading } = useCustomAttributeDefinitions({
    partnerId,
    entity,
  });

  const groups = useMemo<NormalizedCustomAttributeGroup[]>(
    () => normalizeGroups(customAttributeDefinitions),
    [customAttributeDefinitions],
  );

  const groupLabelByKey = useMemo<Map<string, string>>(
    () => new Map(groups.map((group) => [group.key, group.label])),
    [groups],
  );

  const allDefinitions = useMemo<NormalizedCustomAttributeDefinition[]>(
    () => normalizeDefinitions(customAttributeDefinitions, groupLabelByKey),
    [customAttributeDefinitions, groupLabelByKey],
  );

  const userGroupIds = useMemo<Set<string>>(
    () => new Set((user?.userGroups ?? []).map((group) => group.id)),
    [user?.userGroups],
  );

  const visibleDefinitions = useMemo<NormalizedCustomAttributeDefinition[]>(
    () =>
      allDefinitions
        // Lookup fields aren't wired up to render in the side panel yet — hide
        // them until the proper UI lands so empty rows don't bloat the section.
        .filter((definition) => definition.type !== 'lookup_relational')
        .filter((definition) => isVisibleToUser(definition, userGroupIds)),
    [allDefinitions, userGroupIds],
  );

  return { allDefinitions, visibleDefinitions, groups, loading };
}

function normalizeDefinitions(
  raw: CustomAttributeDefinition[] | undefined,
  groupLabelByKey: Map<string, string>,
): NormalizedCustomAttributeDefinition[] {
  return mapAttributesDefinition(raw ?? []).flatMap<NormalizedCustomAttributeDefinition>(
    (attribute) => {
      const typeId = attribute.type?.id;
      if (!isCustomAttributeType(typeId)) {
        // Skip unknown types so a stale frontend doesn't crash on a
        // backend-introduced type. Log so the gap is visible during rollouts.
        if (typeId) {
          console.warn(
            `[useNormalizedCustomAttributeDefinitions] Unsupported custom attribute type "${typeId}" for "${attribute.displayName}"`,
          );
        }
        return [];
      }
      if (!attribute.customAttributeDefinitionId) {
        return [];
      }
      const groupKey = attribute.group ? getCustomAttributeKey(attribute.group) : null;
      return [
        {
          key: attribute.key ?? getCustomAttributeKey(attribute.displayName),
          label: attribute.displayName,
          type: typeId,
          filterable: attribute.filterable ?? false,
          groupKey,
          groupLabel: groupKey ? (groupLabelByKey.get(groupKey) ?? groupKey) : null,
          options: expandLegacyCommaOptions(attribute.options ?? []),
          relatedEntity: attribute.relatedEntity,
          defaultValue: attribute.defaultValue,
          visibility: {
            visibleToAllUsers: attribute.visibility.visibleToAllUsers,
            restrictedToUserGroups: attribute.visibility.restrictedToUserGroups ?? [],
          },
          source: {
            customAttributeDefinitionId: attribute.customAttributeDefinitionId,
          },
        },
      ];
    },
  );
}

function normalizeGroups(
  raw: CustomAttributeDefinition[] | undefined,
): NormalizedCustomAttributeGroup[] {
  return mapAttributesDefinitionGroups(raw).map((group) => ({
    key: getCustomAttributeKey(group.key || group.displayName),
    label: group.displayName,
    source: {
      customAttributeDefinitionId: group.customAttributeDefinitionId,
    },
  }));
}

function isVisibleToUser(
  definition: NormalizedCustomAttributeDefinition,
  userGroupIds: Set<string>,
): boolean {
  if (definition.visibility.visibleToAllUsers) return true;
  return definition.visibility.restrictedToUserGroups.some((groupId) =>
    userGroupIds.has(groupId),
  );
}

/**
 * splits any option whose `value` contains commas / newlines into one option
 * per token. Predates the comma-aware parser in the config form, so older
 * dropdown/multi-select definitions are stored as a single combined option
 * (e.g. `{key: "1_2_3_4_5", value: "1, 2, 3, 4, 5"}`). Selections saved
 * against the old key won't match the new keys — that's an accepted trade-off
 * until those definitions get resaved.
 *
 * Key generation is delegated to the shared `buildOptionsFromLabels` (it slugs each label and
 * disambiguates slug collisions / empty slugs) so the portal and the backend behavior path derive
 * identical keys for the same definition.
 */
function expandLegacyCommaOptions(options: CustomAttributeOption[]): CustomAttributeOption[] {
  return options.flatMap((option) => {
    const tokens = parseOptionsInput(option.value);
    if (tokens.length <= 1) {
      return [option];
    }
    return buildOptionsFromLabels(tokens);
  });
}
